/**
 * SSR-safe lazy loading utility for React components.
 *
 * Supports both **named exports** (pass `exportName` as the 2nd arg) and **default exports**
 * (omit `exportName`). Works correctly with @loadable/babel-plugin for server-side rendering
 * by exporting the function as `loadable`, which the babel plugin recognizes through
 * name-based pattern matching on the local identifier.
 *
 * Adds silent retry on chunk-load failure (1 retry, 1s delay) to recover from transient
 * mobile-network errors. See doc/plans/PRNT-4319-chunk-load-retry.md for analysis.
 *
 * @module @shared/core/loadableFromNamedExport
 */

import type { DefaultComponent, LoadableComponent } from '@loadable/component';
import loadableOriginal from '@loadable/component';
import type { ComponentProps, ComponentType } from 'react';
import type React from 'react';

import globalWindow from '@shared/core/globals';
import { addAction } from '@shared/utils/logger';

// Extracts the original props type from React components without the implicit children prop.
// React.FC<Props> automatically adds `children?: ReactNode` to the props type, but we want
// to preserve the exact props interface as defined by the component author.
// This ensures LoadableComponent<Props> matches the original component's props signature.
// - First checks React.FC<P> and extracts P (the original props without children)
// - Then checks React.ComponentType<P> and extracts P (covers other component patterns)
// - Falls back to ComponentProps<T> for intrinsic elements (div, span, etc.)
// - Returns never for non-component types (should not occur in practice)
type ExtractComponentProps<T> =
  T extends React.FC<infer P>
    ? P
    : T extends React.ComponentType<infer P>
      ? P
      : T extends keyof JSX.IntrinsicElements | React.JSXElementConstructor<any> // eslint-disable-line @typescript-eslint/no-explicit-any
        ? ComponentProps<T>
        : never;

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 1000;
// Upper bound on how long a single retry `<script>` can hang before we abandon it.
// Webpack 4's default `chunkLoadTimeout` is 120s — far too long for a retry budget,
// since the original load has already failed (and timeouts are 21% of chunk errors
// per PRNT-4319). 30s lets a slow-but-recovering network finish while still bounding
// the user-visible "stuck on fallback" window.
const RETRY_SCRIPT_TIMEOUT_MS = 30000;

const isChunkLoadError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const name = (err as { name?: string }).name;
  const message = (err as { message?: string }).message ?? '';
  // The character class includes `/` and `.` because joy-web's named webpack chunks
  // routinely use path-style identifiers (e.g. "content/contactCollector/iframe",
  // "content/admin/photo-upload"). The `name === 'ChunkLoadError'` check covers the
  // normal path; this message fallback exists for re-thrown errors where the name
  // is lost (e.g. wrapped by a transform), and we don't want to silently drop those.
  return name === 'ChunkLoadError' || /Loading chunk [\w/.-]+ failed/.test(message);
};

// Webpack 4 attaches the failed chunk URL as `err.request` (see the JSONP runtime in
// node_modules/webpack/lib/web/JsonpMainTemplate.runtime.js — the Error object stamped on
// chunk load failure has `error.request = realSrc`). We use it to manually re-load the
// chunk with a cache-busting query param without needing access to webpack-internal
// chunk-URL builders (webpack 4 has no `__webpack_require__.u`).
const extractFailedChunkUrl = (err: unknown): string | undefined => {
  const request = (err as { request?: unknown } | null | undefined)?.request;
  return typeof request === 'string' ? request : undefined;
};

const appendQueryParam = (url: string, key: string, value: string | number): string => `${url}${url.includes('?') ? '&' : '?'}${key}=${value}`;

// Manually inject a `<script>` for the chunk URL with a cache-bust query param. When the
// script loads, webpack's JSONP callback (`window["__LOADABLE_LOADED_CHUNKS__"].push(...)`)
// runs from inside the chunk's bootstrap code, which marks `installedChunks[chunkId] = 0`
// and registers all module factories — so a subsequent call to the original `loader()`
// resolves immediately without making a second network request.
const loadScriptWithCacheBust = (failedUrl: string, attempt: number): Promise<void> =>
  new Promise((resolve, reject) => {
    const url = appendQueryParam(failedUrl, 'retry', attempt);
    // `document` is the canonical reference here. SSR never reaches this path (async chunks
    // load synchronously via `requireSync` on the server), but guard defensively so an
    // accidental SSR call fails loudly instead of crashing with "document is not defined".
    if (typeof document === 'undefined') {
      reject(new Error('Chunk retry attempted without a DOM'));
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.crossOrigin = 'anonymous';
    let timeoutId: ReturnType<typeof globalWindow.setTimeout> | undefined;
    const cleanup = () => {
      script.onload = null;
      script.onerror = null;
      if (timeoutId !== undefined) {
        globalWindow.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    script.onload = () => {
      cleanup();
      resolve();
    };
    script.onerror = () => {
      cleanup();
      script.remove();
      reject(new Error(`Chunk retry script failed: ${url}`));
    };
    // Without this, a stalled response (the 21% timeout-class failures we're meant to
    // recover) would leave the wrapper awaiting `onload`/`onerror` forever and the lazy
    // route stuck on its fallback. Mirrors webpack's own chunk-load-timeout behavior.
    timeoutId = globalWindow.setTimeout(() => {
      cleanup();
      script.remove();
      reject(new Error(`Chunk retry script timed out: ${url}`));
    }, RETRY_SCRIPT_TIMEOUT_MS);
    document.head.appendChild(script);
  });

// Wraps the chunk-load promise with retry. Orthogonal to `resolveComponent` (which runs
// AFTER the chunk loads successfully). The `chunkName` (when known) is sent with telemetry
// and lets us facet retry recovery rates by chunk in New Relic.
const withChunkRetry = <T>(loader: () => Promise<T>, chunkName?: string): (() => Promise<T>) => {
  const run = (n: number): Promise<T> =>
    loader().catch((err: unknown) => {
      if (!isChunkLoadError(err) || n >= MAX_RETRIES) {
        if (isChunkLoadError(err)) {
          addAction('ChunkRetryExhausted', { chunkName, attempts: n });
        }
        throw err;
      }
      const attempt = n + 1;
      const failedUrl = extractFailedChunkUrl(err);
      addAction('ChunkRetryAttempt', { chunkName, attempt, cacheBust: Boolean(failedUrl) });

      return new Promise<T>((resolve, reject) => {
        globalWindow.setTimeout(async () => {
          // If we have the failed URL, pre-load the chunk with a cache-bust query — this
          // recovers from CDN-edge-cache corruption (the Sydney pattern). When the script
          // loads, webpack's JSONP callback marks the chunk installed, so the loader() call
          // below resolves immediately from cache without a second network round-trip.
          // If the cache-bust script itself fails (e.g. still offline, DevTools block,
          // hard server failure) we treat retry as exhausted here — the recursive run(n+1)
          // is never reached, so we have to emit the Exhausted telemetry ourselves. We
          // propagate the *original* chunk error so callers / error boundaries / NR see a
          // ChunkLoadError, not the internal "Chunk retry script failed" wrapper.
          if (failedUrl) {
            try {
              await loadScriptWithCacheBust(failedUrl, attempt);
            } catch {
              addAction('ChunkRetryExhausted', { chunkName, attempts: attempt });
              reject(err);
              return;
            }
          }
          try {
            const result = await run(n + 1);
            addAction('ChunkRetrySuccess', { chunkName, attempt });
            resolve(result);
          } catch (retryErr) {
            // run(n + 1) emits Exhausted internally before throwing; no double-log here.
            reject(retryErr);
          }
        }, RETRY_DELAY_MS);
      });
    });
  return () => run(0);
};

// `@loadable/babel-plugin` rewrites `() => import('./X')` into a config object
// { requireAsync, requireSync, chunkName, resolved } in production builds. Handle both shapes.
type BabelLoadableConfig = {
  requireAsync: (...args: unknown[]) => Promise<unknown>;
  chunkName?: () => string;
  [key: string]: unknown;
};

const wrapLoaderArg = (arg: unknown): unknown => {
  if (typeof arg === 'function') {
    return withChunkRetry(arg as () => Promise<unknown>);
  }
  if (arg && typeof arg === 'object' && typeof (arg as BabelLoadableConfig).requireAsync === 'function') {
    const config = arg as BabelLoadableConfig;
    const chunkName = typeof config.chunkName === 'function' ? config.chunkName() : undefined;
    const originalRequireAsync = config.requireAsync;
    // The babel-plugin's `requireAsync` is a method that calls `this.resolve(...)` and
    // friends — wrap it as a `function` (not an arrow) so we can preserve `this` from the
    // call site (which is the config object loadable hands back to us) and forward args.
    const wrappedRequireAsync = function (this: unknown, ...args: unknown[]) {
      // The inner arrow inherits `this` lexically from this `function`, so `apply(this, …)`
      // forwards the caller's `this` (the config object loadable hands back) to the original.
      return withChunkRetry(() => originalRequireAsync.apply(this, args), chunkName)();
    };
    return { ...config, requireAsync: wrappedRequireAsync };
  }
  return arg;
};

/**
 * Create a lazy-loaded React component.
 *
 * **For default exports** — omit `exportName`:
 * ```typescript
 * const MyComp = loadable(() => import('./MyComp'));
 * ```
 *
 * **For named exports** — pass the export name as the 2nd argument:
 * ```typescript
 * const MyComp = loadable(() => import('./MyComp'), 'MyComp');
 * ```
 *
 * SSR-safe because:
 * 1. Exported with the name `loadable`, which `@loadable/babel-plugin` recognizes
 *    through name-based pattern matching on the local identifier.
 * 2. Named-export path uses `resolveComponent` (not `{ default: ... }` wrapping)
 *    to avoid hoist-non-react-statics race conditions.
 *
 * Both code paths silently retry once on chunk-load failure with a 1s delay and a
 * cache-busting query param. See doc/plans/PRNT-4319-chunk-load-retry.md.
 *
 * **Do NOT rename the import** — the babel plugin requires the local identifier `loadable`:
 * ```typescript
 * import { loadable } from '@shared/core/loadableFromNamedExport'; // OK
 * import { loadable as loadableX } from '@shared/core/loadableFromNamedExport'; // BREAKS SSR
 * ```
 *
 * `loadable.lib()` is a static method on `@loadable/component`'s default export and is
 * not wrapped here. Callers that need `.lib()` should continue importing
 * `@loadable/component` directly.
 *
 * @see https://loadable-components.com/docs/api-loadable-component/
 * @see https://github.com/gregberge/loadable-components/issues/420
 */
// Mirrors @loadable/component's `OptionsWithoutResolver<Props>`. `resolveComponent` is
// owned by the named-export overload (set internally from `exportName`), so it's not
// exposed here.
type LoadableOptions<Props = unknown> = Readonly<{
  fallback?: JSX.Element;
  ssr?: boolean;
  cacheKey?: (props: Props) => unknown;
}>;

// Overload A: default-export (omit `exportName`)
export function loadable<P>(importFn: () => Promise<{ default: ComponentType<P> }>, options?: LoadableOptions): LoadableComponent<P>;

// Overload B: named-export
export function loadable<TModule extends Record<string, unknown>, TExportName extends keyof TModule & string>(
  importFn: () => Promise<TModule>,
  exportName: TExportName,
  options?: LoadableOptions,
): LoadableComponent<ExtractComponentProps<TModule[TExportName]>>;

// Implementation — the public API surface is constrained by the overloads above; this body
// uses `unknown` at the input boundary and casts at the single point where we hand off to
// @loadable/component (which accepts either a thunk OR the babel-plugin-transformed config
// object as its first arg — a runtime contract not reflected in its TS types).
export function loadable(importFn: unknown, second?: string | LoadableOptions, third?: LoadableOptions): LoadableComponent<unknown> {
  const wrappedLoader = wrapLoaderArg(importFn) as () => Promise<unknown>;
  if (typeof second === 'string') {
    const exportName = second;
    const options = third;
    return loadableOriginal(wrappedLoader, {
      resolveComponent: (m: unknown) => (m as Record<string, ComponentType<unknown>>)[exportName],
      ...options,
    });
  }
  // The no-resolver overload of @loadable/component requires the loader to resolve to a
  // `DefaultComponent` (module with a default export). The runtime accepts the babel-plugin
  // config object too — cast to satisfy the stricter typed signature.
  return loadableOriginal(wrappedLoader as () => Promise<DefaultComponent<unknown>>, { ...second });
}

// Internal exports for unit testing only.
export const __testing__ = { isChunkLoadError, withChunkRetry, wrapLoaderArg };
